import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { FiLogOut, FiUser, FiRefreshCw, FiFilter, FiPlus } from 'react-icons/fi';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import LoadingSpinner from '../components/LoadingSpinner';
import EventModal from '../components/EventModal';
import toast from 'react-hot-toast';

const localizer = momentLocalizer(moment);

const Calendar = () => {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filterProvider, setFilterProvider] = useState('all');
  const todayKey = useMemo(() => moment().format('YYYY-MM-DD'), []);

  // Fetch events
  const { data: events = [], isLoading } = useQuery(
    ['events', filterProvider],
    async () => {
      const params = new URLSearchParams();
      if (filterProvider !== 'all') {
        params.append('provider', filterProvider);
      }
      const response = await api.get(`/calendar/events?${params.toString()}`);
      return response.data.events;
    }
  );

  // Sync calendar mutation
  const syncMutation = useMutation(
    async (provider) => {
      const response = await api.post(`/calendar/sync/${provider}`);
      return response.data;
    },
    {
      onSuccess: (data, provider) => {
        toast.success(`${provider} calendar synced successfully!`);
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('calendarStatus');
      },
      onError: (error, provider) => {
        toast.error(`Failed to sync ${provider} calendar: ${error.response?.data?.message || 'Unknown error'}`);
      },
    }
  );

  // Convert events to calendar format
  const calendarEvents = useMemo(() => {
    return events.map(event => {
      const classes = [`event-${event.calendarProvider}`];
      if (event.notificationsSent?.some(notification => notification.status === 'success')) {
        classes.push('event-notified');
      }

      return {
        id: event.id,
        title: event.title,
        start: new Date(event.startTime),
        end: new Date(event.endTime),
        resource: event,
        className: classes.join(' ')
      };
    });
  }, [events]);

  const handleEventSelect = (event) => {
    setSelectedEvent(event.resource);
    setIsModalOpen(true);
  };

  const handleSelectSlot = ({ start }) => {
    setSelectedEvent(null);
    setSelectedDate(start);
    setIsModalOpen(true);
  };

  const handleCreateMeeting = () => {
    setSelectedEvent(null);
    setSelectedDate(new Date());
    setIsModalOpen(true);
  };

  const handleSyncCalendar = (provider) => {
    syncMutation.mutate(provider);
  };

  const eventStyleGetter = (event) => {
    const provider = event.resource?.calendarProvider;
    let style = {
      backgroundColor: '#3B82F6',
      borderColor: '#2563EB',
    };

    if (provider === 'google') {
      style.backgroundColor = '#DC2626';
      style.borderColor = '#B91C1C';
    } else if (provider === 'microsoft') {
      style.backgroundColor = '#2563EB';
      style.borderColor = '#1D4ED8';
    }

    return { style };
  };

  const dayPropGetter = (date) => {
    const isToday = moment(date).format('YYYY-MM-DD') === todayKey;

    if (!isToday) {
      return {};
    }

    return {
      style: {
        backgroundColor: '#dbeafe',
        boxShadow: 'inset 0 0 0 2px #2563eb',
        borderRadius: '14px'
      },
      className: 'fn-calendar-today-cell'
    };
  };

  const components = useMemo(() => ({
    month: {
      dateHeader: ({ date, label }) => {
        const isToday = moment(date).format('YYYY-MM-DD') === todayKey;

        if (!isToday) {
          return <span>{label}</span>;
        }

        return (
          <span className="inline-flex min-h-[2rem] min-w-[2rem] items-center justify-center rounded-full bg-blue-600 px-2 font-semibold text-white shadow-sm">
            {label}
          </span>
        );
      }
    }
  }), [todayKey]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/futurenote-icon.svg" alt="FutureNote" className="h-9 w-9" />
              <h1 className="ml-2 text-xl font-semibold text-gray-900">Calendar</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                Dashboard
              </Link>

              {/* Filter */}
              <div className="flex items-center space-x-2">
                <FiFilter className="h-4 w-4 text-gray-400" />
                <select
                  value={filterProvider}
                  onChange={(e) => setFilterProvider(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1"
                >
                  <option value="all">All Calendars</option>
                  <option value="google">Google</option>
                  <option value="microsoft">Microsoft</option>
                </select>
              </div>

              <button
                onClick={handleCreateMeeting}
                className="btn-primary text-sm px-3 py-1 inline-flex items-center"
              >
                <FiPlus className="h-4 w-4 mr-1" />
                New meeting
              </button>

              {/* Sync Buttons */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleSyncCalendar('google')}
                  disabled={syncMutation.isLoading}
                  className="btn-secondary text-sm px-3 py-1"
                >
                  {syncMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <FiRefreshCw className="h-3 w-3 mr-1" />
                      Sync Google
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => handleSyncCalendar('microsoft')}
                  disabled={syncMutation.isLoading}
                  className="btn-secondary text-sm px-3 py-1"
                >
                  {syncMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <FiRefreshCw className="h-3 w-3 mr-1" />
                      Sync MS
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center space-x-2">
                <FiUser className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-700">
                  {user?.firstName} {user?.lastName}
                </span>
              </div>
              
              <button
                onClick={logout}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiLogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Calendar Legend */}
        <div className="mb-6 flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-600 rounded-full"></div>
            <span className="text-sm text-gray-600">Google Calendar</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
            <span className="text-sm text-gray-600">Microsoft Calendar</span>
          </div>
        </div>

        {/* Calendar */}
        <div className="card p-0">
          {isLoading ? (
            <div className="flex justify-center items-center h-96">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="h-96">
              <BigCalendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                onSelectEvent={handleEventSelect}
                onSelectSlot={handleSelectSlot}
                selectable
                eventPropGetter={eventStyleGetter}
                dayPropGetter={dayPropGetter}
                components={components}
                views={['month', 'week', 'day']}
                defaultView="month"
                step={60}
                timeslots={1}
                tooltipAccessor={(event) => `${event.title} - ${event.resource?.location || 'No location'}`}
              />
            </div>
          )}
        </div>

        {/* Event Modal */}
        {isModalOpen && (
          <EventModal
            event={selectedEvent}
            selectedDate={selectedDate}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedEvent(null);
              setSelectedDate(null);
            }}
            onEventUpdate={() => {
              queryClient.invalidateQueries('events');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Calendar;


